import React, { useLayoutEffect, useRef, useState } from "react";
import {
  Mail,
  MessageCircle,
  Clock,
  Send,
  CheckCircle2,
  ArrowRight,
  Copy,
  Check,
  AlertCircle,
} from "lucide-react";
import emailjs from "@emailjs/browser";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function ContactSection() {
  const sectionRef = useRef(null);
  const formRef = useRef(null);
  const cardRefs = useRef([]);

  const [copied, setCopied] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    type: "success",
    message: "",
  });

  const CONTACT_EMAIL = "support@slipsense.in";

  const showToast = (type, message) => {
    setToast({
      show: true,
      type,
      message,
    });

    setTimeout(() => {
      setToast({
        show: false,
        type: "success",
        message: "",
      });
    }, 3500);
  };

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".contact-eyebrow, .contact-title, .contact-subtitle",
        {
          opacity: 0,
          y: 32,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.85,
          ease: "power3.out",
          stagger: 0.12,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 72%",
          },
        }
      );

      gsap.fromTo(
        cardRefs.current,
        {
          opacity: 0,
          y: 44,
          scale: 0.96,
          rotate: -1.5,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          rotate: 0,
          duration: 0.8,
          ease: "power3.out",
          stagger: 0.12,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 58%",
          },
        }
      );

      gsap.fromTo(
        formRef.current,
        {
          opacity: 0,
          x: 70,
          scale: 0.96,
        },
        {
          opacity: 1,
          x: 0,
          scale: 1,
          duration: 0.95,
          ease: "power4.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 50%",
          },
        }
      );

      gsap.to(".contact-floating-note", {
        y: -14,
        rotate: 2,
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".contact-floating-bubble", {
        y: 12,
        rotate: -2,
        duration: 3.5,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2200);
    } catch (error) {
      console.log("Could not copy email:", error);
    }
  };
const handleSubmit = async (e) => {
  e.preventDefault();

  if (isSending) return;

  const form = new FormData(e.currentTarget);

  const name = form.get("name")?.trim() || "";
  const email = form.get("email")?.trim() || "";
  const business = form.get("business")?.trim() || "";
  const userMessage = form.get("message")?.trim() || "";

  if (!name || !email || !userMessage) {
    showToast("error", "Please fill your name, email and message.");
    return;
  }

  setIsSending(true);

  try {
    await emailjs.send(
      import.meta.env.VITE_EMAILJS_SERVICE_ID,
      import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
      {
        title: business
          ? `Payroll inquiry from ${business}`
          : `Payroll inquiry from ${name}`,

        name: name,
        email: email,

        message: `Business name: ${business || "Not provided"}

Message:
${userMessage}`,

        time: new Date().toLocaleString("en-IN", {
          dateStyle: "medium",
          timeStyle: "short",
        }),
      },
      {
        publicKey: import.meta.env.VITE_EMAILJS_PUBLIC_KEY,
      }
    );

    e.currentTarget.reset();

    showToast(
      "success",
      "We got you, my friend. Your payroll message has been sent successfully."
    );
  } catch (error) {
    console.error("Email send failed:", error);

    showToast(
      "error",
      "Message could not be sent right now. Please try again or email us directly."
    );
  } finally {
    setIsSending(false);
  }
};
  return (
    <section ref={sectionRef} id="contact" className="contact-section">
      {toast.show && (
        <div className={`contact-toast contact-toast-${toast.type}`}>
          <div className="contact-toast-icon">
            {toast.type === "success" ? (
              <Check size={18} />
            ) : (
              <AlertCircle size={18} />
            )}
          </div>

          <p>{toast.message}</p>
        </div>
      )}

      <div className="contact-orb contact-orb-left" />
      <div className="contact-orb contact-orb-right" />

      <div className="contact-container">
        <div className="contact-header">
          <span className="contact-eyebrow">Contact</span>

          <h2 className="contact-title">Ready to simplify your payroll?</h2>

          <p className="contact-subtitle">
            Tell us about your business and payroll flow. We’ll help you
            understand how SlipSense can manage attendance, deductions,
            payslips and salary proof in one place.
          </p>
        </div>

        <div className="contact-grid">
          <div className="contact-left">
            <div
              className="contact-dark-card"
              ref={(el) => (cardRefs.current[0] = el)}
            >
              <div className="contact-dark-icon">
                <MessageCircle size={26} />
              </div>

              <h3>Talk to us before setting up payroll.</h3>

              <p>
                Whether you run a shop, clinic, agency, contractor team or
                small office, we can help you set up a simple payroll process.
              </p>

              <div className="contact-check-list">
                <div>
                  <CheckCircle2 size={18} />
                  <span>Payroll setup guidance</span>
                </div>

                <div>
                  <CheckCircle2 size={18} />
                  <span>Attendance and leave flow</span>
                </div>

                <div>
                  <CheckCircle2 size={18} />
                  <span>Payslip and salary proof automation</span>
                </div>
              </div>
            </div>

            <div className="contact-info-grid">
              <button
                type="button"
                className="contact-info-card contact-info-button"
                ref={(el) => (cardRefs.current[1] = el)}
                onClick={copyEmail}
              >
                <Mail size={21} />

                <span>Email</span>

                <strong>{CONTACT_EMAIL}</strong>

     
              </button>

              <div
                className="contact-info-card"
                ref={(el) => (cardRefs.current[2] = el)}
              >
                <Clock size={21} />
                <span>Reply time</span>
                <strong>Within 24 hours</strong>
              </div>
            </div>

            <div className="contact-floating-note">
              <span>Best for</span>
              <strong>Small businesses managing payroll manually</strong>
            </div>
          </div>

          <div className="contact-form-shell" ref={formRef}>
            <div className="contact-floating-bubble">
              <span>Need help?</span>
              <strong>Send your payroll flow</strong>
            </div>

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="contact-form-top">
                <div>
                  <p>Get in touch</p>
                  <h3>Let’s make payroll clear.</h3>
                </div>

                <div className="contact-form-icon">
                  <Send size={22} />
                </div>
              </div>

              <div className="contact-field-grid">
                <label>
                  <span>Your name</span>
                  <input
                    name="name"
                    type="text"
                    placeholder="Your name"
                    required
                  />
                </label>

                <label>
                  <span>Email</span>
                  <input
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    required
                  />
                </label>
              </div>

              <label>
                <span>Business name</span>
                <input
                  name="business"
                  type="text"
                  placeholder="e.g. HubStudio, retail shop, clinic"
                />
              </label>

              <label>
                <span>What do you need help with?</span>
                <textarea
                  name="message"
                  rows="5"
                  placeholder="Tell us your team size, salary process, attendance/leave tracking, and payslip needs..."
                  required
                />
              </label>

              <button
                type="submit"
                className="contact-submit"
                disabled={isSending}
              >
                {isSending ? "Sending..." : "Send message"}
                <ArrowRight size={18} />
              </button>

              <p className="contact-note">
                After sending, we’ll reply to your email with the next steps.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}